import React, { useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Skeleton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Visibility,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  Payment,
  FilterList,
  Clear,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { paymentVoucherApi, PaymentVoucher, brokerApi, bookingApi, propertyApi, currencyApi } from '../services/api';
import { formatDate } from '../utils/formatters';

const PaymentVouchersPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    status: '',
    broker_id: '' as string,
    from_date: '',
    to_date: '',
  });

  // Create voucher dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    broker_id: '',
    booking_id: '',
    payment_amount: '',
    payment_date: '',
    payment_method: 'CASH' as 'CASH' | 'BANK_TRANSFER' | 'CHECK' | 'CREDIT_CARD' | 'OTHER',
    description: '',
    currency_id: '',
    property_id: '',
  });

  // Get payment vouchers
  const {
    data: vouchersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['payment-vouchers', filters],
    queryFn: () => paymentVoucherApi.getAll({
      ...filters,
      broker_id: filters.broker_id ? parseInt(filters.broker_id) : undefined
    }),
  });

  // Get brokers for create form
  const { data: brokersData } = useQuery({
    queryKey: ['brokers'],
    queryFn: () => brokerApi.getAll(),
  });

  // Get properties for create form
  const { data: propertiesData } = useQuery({
    queryKey: ['properties'],
    queryFn: () => propertyApi.getAll(),
  });

  // Get currencies for create form
  const { data: currenciesData } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => currencyApi.getAll(),
  });

  // Get all bookings and filter by broker
  const { data: bookingsData, isLoading: bookingsLoading } = useQuery({
    queryKey: ['bookings'],
    queryFn: () => bookingApi.getAll(),
  });

  const vouchers = vouchersData?.data?.data || [];
  const pagination = vouchersData?.data;
  const brokers = brokersData?.data || [];
  const properties = propertiesData || [];
  const currencies = currenciesData?.data || [];
  const bookings = bookingsData || [];

  // Filter bookings by selected broker and commission
  const bookingsWithCommission = bookings
    .filter((booking: any) => {
      const hasCommission = booking.commission_amount && booking.commission_amount > 0;
      const matchesBroker = !createForm.broker_id || booking.broker_id?.toString() === createForm.broker_id;
      return hasCommission && matchesBroker;
    });



  // Mutations
  const createMutation = useMutation({
    mutationFn: paymentVoucherApi.create,
    onSuccess: (data) => {
      console.log('Voucher created successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['payment-vouchers'] });
      setCreateDialogOpen(false);
      resetCreateForm();
    },
    onError: (error) => {
      console.error('Error creating voucher:', error);
      alert('Failed to create voucher. Check console for details.');
    },
  });

  const approveMutation = useMutation({
    mutationFn: paymentVoucherApi.approve,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-vouchers'] });
    },
  });

  const payMutation = useMutation({
    mutationFn: paymentVoucherApi.pay,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-vouchers'] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: paymentVoucherApi.cancel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-vouchers'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: paymentVoucherApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-vouchers'] });
    },
  });

  const resetCreateForm = () => {
    setCreateForm({
      broker_id: '',
      booking_id: '',
      payment_amount: '',
      payment_date: '',
      payment_method: 'CASH',
      description: '',
      currency_id: '',
      property_id: '',
    });
  };

  const handleCreateVoucher = () => {
    console.log('=== CREATE VOUCHER DEBUG ===');
    console.log('Form data:', createForm);
    console.log('Form property_id type:', typeof createForm.property_id, 'value:', createForm.property_id);
    console.log('Properties:', properties);
    console.log('Properties IDs:', Array.isArray(properties) ? properties.map(p => ({ id: p.id, type: typeof p.id, name: p.name })) : 'Not array');
    console.log('Currencies:', currencies);
    
    const selectedBooking = createForm.booking_id ? bookingsWithCommission.find((b: any) => b.id.toString() === createForm.booking_id) : null;
    const selectedProperty = Array.isArray(properties) ? properties.find((p: any) => p.id.toString() === createForm.property_id) : null;
    
    console.log('Selected booking:', selectedBooking);
    console.log('Selected property:', selectedProperty);
    
    if (!selectedProperty) {
      console.error('No property selected!');
      return;
    }

    const voucherData = {
      broker_id: parseInt(createForm.broker_id),
      booking_id: createForm.booking_id ? parseInt(createForm.booking_id) : undefined,
      payment_amount: parseFloat(createForm.payment_amount),
      payment_date: createForm.payment_date,
      payment_method: createForm.payment_method,
      description: createForm.description,
      currency_id: selectedProperty.currency_id || currencies.find((c: any) => c.is_default)?.id || 1,
      property_id: parseInt(createForm.property_id),
    };

    console.log('Voucher data to send:', voucherData);
    console.log('=== END DEBUG ===');

    createMutation.mutate(voucherData);
  };

  const handleCreateDialogOpen = () => {
    setCreateDialogOpen(true);
    // Set default date to today
    setCreateForm(prev => ({
      ...prev,
      payment_date: new Date().toISOString().split('T')[0],
    }));
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
    resetCreateForm();
  };

  const handleApprove = (voucherId: number) => {
    if (window.confirm(t('confirm_approve_voucher'))) {
      approveMutation.mutate(voucherId);
    }
  };

  const handlePay = (voucherId: number) => {
    if (window.confirm(t('confirm_pay_voucher'))) {
      payMutation.mutate(voucherId);
    }
  };

  const handleCancel = (voucherId: number) => {
    if (window.confirm(t('confirm_cancel_voucher'))) {
      cancelMutation.mutate(voucherId);
    }
  };

  const handleDelete = (voucherId: number) => {
    if (window.confirm(t('confirm_delete_voucher'))) {
      deleteMutation.mutate(voucherId);
    }
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      broker_id: '',
      from_date: '',
      to_date: '',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'default';
      case 'PENDING':
        return 'warning';
      case 'APPROVED':
        return 'info';
      case 'PAID':
        return 'success';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return t('draft');
      case 'PENDING':
        return t('pending');
      case 'APPROVED':
        return t('approved');
      case 'PAID':
        return t('paid');
      case 'CANCELLED':
        return t('cancelled');
      default:
        return status;
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'CASH':
        return t('cash');
      case 'BANK_TRANSFER':
        return t('bank_transfer');
      case 'CHECK':
        return t('check');
      case 'CREDIT_CARD':
        return t('credit_card');
      case 'OTHER':
        return t('other');
      default:
        return method;
    }
  };

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {t('error_loading_vouchers')}: {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('payment_vouchers')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateDialogOpen}
        >
          {t('create_voucher')}
        </Button>
      </Box>

      {/* Create Voucher Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCreateDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>{t('create_payment_voucher')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('broker')}</InputLabel>
                <Select
                  value={createForm.broker_id}
                  onChange={(e) => {
                    console.log('Broker selected:', e.target.value, typeof e.target.value);
                    setCreateForm({ ...createForm, broker_id: e.target.value, booking_id: '' });
                  }}
                  label={t('broker')}
                >
                  {brokers.length === 0 ? (
                    <MenuItem disabled>{t('loading')}...</MenuItem>
                  ) : (
                    brokers.map((broker: any) => (
                      <MenuItem key={broker.id} value={broker.id.toString()}>
                        {broker.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {brokers.length === 0 && (
                  <Box sx={{ mt: 1, fontSize: '0.8rem', color: 'text.secondary' }}>
                    No brokers available
                  </Box>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('property')}</InputLabel>
                <Select
                  value={createForm.property_id}
                  onChange={(e) => {
                    console.log('Property selected:', e.target.value, typeof e.target.value);
                    setCreateForm({ ...createForm, property_id: e.target.value });
                  }}
                  label={t('property')}
                >
                  {Array.isArray(properties) ? properties.map((property: any) => (
                    <MenuItem key={property.id} value={property.id.toString()}>
                      {property.name}
                    </MenuItem>
                  )) : null}
                </Select>
              </FormControl>
            </Grid>

                        <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('booking')} ({t('optional')})</InputLabel>
                <Select
                  value={createForm.booking_id}
                  onChange={(e) => {
                    const booking = bookingsWithCommission.find((b: any) => b.id.toString() === e.target.value);
                    setCreateForm({ 
                      ...createForm, 
                      booking_id: e.target.value,
                      payment_amount: booking ? (booking.commission_amount || 0).toString() : ''
                    });
                  }}
                  label={`${t('booking')} (${t('optional')})`}
                  disabled={!createForm.broker_id || bookingsLoading}
                >
                  <MenuItem value="">
                    <em>{t('no_booking_selected')}</em>
                  </MenuItem>
                  {bookingsLoading ? (
                    <MenuItem disabled>{t('loading')}...</MenuItem>
                  ) : bookingsWithCommission.length === 0 ? (
                    <MenuItem disabled>
                      {!createForm.broker_id 
                        ? t('select_broker_first') 
                        : t('no_bookings_with_commission')
                      }
                    </MenuItem>
                  ) : (
                    bookingsWithCommission.map((booking: any) => (
                      <MenuItem key={booking.id} value={booking.id}>
                        #{booking.booking_number || booking.id} - {booking.tenant_name} ({booking.commission_amount || 0})
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('payment_amount')}
                type="number"
                value={createForm.payment_amount}
                onChange={(e) => setCreateForm({ ...createForm, payment_amount: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('payment_date')}
                type="date"
                value={createForm.payment_date}
                onChange={(e) => setCreateForm({ ...createForm, payment_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('payment_method')}</InputLabel>
                <Select
                  value={createForm.payment_method}
                  onChange={(e) => setCreateForm({ ...createForm, payment_method: e.target.value as any })}
                  label={t('payment_method')}
                >
                  <MenuItem value="CASH">{t('cash')}</MenuItem>
                  <MenuItem value="BANK_TRANSFER">{t('bank_transfer')}</MenuItem>
                  <MenuItem value="CHECK">{t('check')}</MenuItem>
                  <MenuItem value="CREDIT_CARD">{t('credit_card')}</MenuItem>
                  <MenuItem value="OTHER">{t('other')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('description')}
                multiline
                rows={3}
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateDialogClose}>{t('cancel')}</Button>
          <Button
            onClick={handleCreateVoucher}
            variant="contained"
            disabled={createMutation.isPending || !createForm.broker_id || !createForm.payment_amount || !createForm.payment_date || !createForm.property_id}
            startIcon={createMutation.isPending ? <CircularProgress size={20} /> : null}
          >
            {createMutation.isPending ? t('creating') : t('create')}
          </Button>
          <Button
            onClick={() => {
              console.log('Test button clicked');
              console.log('Current form state:', createForm);
              console.log('Available brokers:', brokers);
              console.log('Available properties:', properties);
            }}
            variant="outlined"
            size="small"
            sx={{ ml: 1 }}
          >
            Test Form
          </Button>
          {/* Debug info */}
          <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1, fontSize: '0.8rem' }}>
            Debug: Broker: {createForm.broker_id || 'Not selected'} | 
            Amount: {createForm.payment_amount || 'Not set'} | 
            Date: {createForm.payment_date || 'Not set'} | 
            Property: {createForm.property_id || 'Not selected'} ({typeof createForm.property_id}) |
            Pending: {createMutation.isPending ? 'Yes' : 'No'}
          </Box>

        </DialogActions>
      </Dialog>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <FilterList />
            <Typography variant="h6">{t('filters')}</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('status')}</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  label={t('status')}
                >
                  <MenuItem value="">{t('all')}</MenuItem>
                  <MenuItem value="DRAFT">{t('draft')}</MenuItem>
                  <MenuItem value="PENDING">{t('pending')}</MenuItem>
                  <MenuItem value="APPROVED">{t('approved')}</MenuItem>
                  <MenuItem value="PAID">{t('paid')}</MenuItem>
                  <MenuItem value="CANCELLED">{t('cancelled')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('from_date')}
                type="date"
                value={filters.from_date}
                onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('to_date')}
                type="date"
                value={filters.to_date}
                onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  startIcon={<Clear />}
                >
                  {t('clear')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Vouchers Table */}
      <Card>
        <CardContent>
          {isLoading ? (
            <Box>
              {[...Array(5)].map((_, index) => (
                <Skeleton key={index} variant="rectangular" height={60} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : vouchers.length === 0 ? (
            <Alert severity="info">{t('no_vouchers_found')}</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('voucher_number')}</TableCell>
                    <TableCell>{t('broker')}</TableCell>
                    <TableCell>{t('booking')}</TableCell>
                    <TableCell>{t('commission_amount')}</TableCell>
                    <TableCell>{t('payment_amount')}</TableCell>
                    <TableCell>{t('payment_date')}</TableCell>
                    <TableCell>{t('payment_method')}</TableCell>
                    <TableCell>{t('status')}</TableCell>
                    <TableCell>{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vouchers.map((voucher: PaymentVoucher) => (
                    <TableRow key={voucher.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {voucher.voucher_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{voucher.broker?.name}</TableCell>
                      <TableCell>
                        #{voucher.booking?.booking_number || voucher.booking?.id}
                      </TableCell>
                      <TableCell>
                        {voucher.currency?.symbol || '$'}{voucher.commission_amount.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {voucher.currency?.symbol || '$'}{voucher.payment_amount.toFixed(2)}
                      </TableCell>
                      <TableCell>{formatDate(voucher.payment_date)}</TableCell>
                      <TableCell>{getPaymentMethodLabel(voucher.payment_method)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(voucher.status)}
                          color={getStatusColor(voucher.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title={t('view')}>
                            <IconButton size="small">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          
                          {voucher.status === 'PENDING' && (
                            <Tooltip title={t('approve')}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleApprove(voucher.id)}
                                disabled={approveMutation.isPending}
                              >
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {voucher.status === 'APPROVED' && (
                            <Tooltip title={t('pay')}>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handlePay(voucher.id)}
                                disabled={payMutation.isPending}
                              >
                                <Payment />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {(voucher.status === 'DRAFT' || voucher.status === 'PENDING') && (
                            <>
                              <Tooltip title={t('edit')}>
                                <IconButton size="small" color="primary">
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('cancel')}>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleCancel(voucher.id)}
                                  disabled={cancelMutation.isPending}
                                >
                                  <Cancel />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          
                          {voucher.status === 'DRAFT' && (
                            <Tooltip title={t('delete')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(voucher.id)}
                                disabled={deleteMutation.isPending}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaymentVouchersPage;
